/**
 * Onglet « Rapprochés » — le journal de ce qui a été écrit, les deux sens.
 *
 * Il répond à un manque signalé au premier essai réel : la file ne montre que
 * ce qui **reste** à faire, donc un import parfaitement réussi se traduisait
 * par un écran vide, indistinguable d'un import qui n'aurait rien écrit du
 * tout.
 *
 * Le journal montre chaque mouvement, dit **qui l'a décidé** — la machine par
 * une référence lue, ou un humain — et permet de défaire une affectation.
 * C'est la contrepartie nécessaire de l'automatisme : on ne fait confiance
 * qu'à ce qu'on peut vérifier et annuler.
 *
 * ## Ce que cette version change, et pourquoi
 *
 * Demandé le 6 septembre 2026 : filtrer par entrées ou par dépenses, et
 * chercher par numéro de compte émetteur, par date, par communication, par
 * mot-clé, par numéro de facture ou de note de crédit.
 *
 * **La recherche se fait en base.** L'écran lisait les cent dernières
 * affectations et filtrait ensuite dans le navigateur. Mesuré en production :
 * le journal porte **4 152 entrées et 45 dépenses**. Chercher un IBAN côté
 * navigateur n'aurait donc interrogé que les cent dernières lignes, et rendu
 * « rien trouvé » sur un compte présent quatre mille lignes plus loin.
 *
 * Un écran qui dit « rien trouvé » alors que la ligne existe est **pire** qu'un
 * écran sans recherche : on en tire une conclusion, et ici la conclusion serait
 * qu'un remboursement n'a pas été fait.
 *
 * ## Les dépenses, et ce qu'elles révèlent
 *
 * Une dépense est un remboursement sorti du compte. Le rapprochement n'en
 * retient que celles qui **citent quelque chose** — un numéro de note de
 * crédit, ou une référence de facture ; le reste des virements émis (paiements
 * de volontariat, frais) n'entre pas dans la comptabilité des familles.
 *
 * La colonne « Famille » a longtemps montré toutes les dépenses en « non
 * rattaché », parce que la vue la lisait de `refunds.user_id`, qui n'est jamais
 * écrit : un extrait de banque ne porte pas de famille. Elle se lit maintenant
 * du **placement** — la provision, la note ou la facture le disent — et
 * « non rattaché » redevient ce qu'il aurait toujours dû être : le signe qu'un
 * virement n'a été imputé à rien.
 *
 * ## Une ligne du journal est une DÉCISION, pas un mouvement
 *
 * Des deux côtés désormais. Un virement partagé en deux placements donne deux
 * lignes, comme un encaissement partagé en donne deux. C'est ce qui rend
 * « Défaire » possible sur une dépense : on défait une imputation, jamais un
 * virement. Voir `20260921075716_defaire_une_depense_aussi`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowDownLeft, ArrowDownUp, ArrowUpRight, ChevronDown, ChevronRight,
  FileCheck2, Inbox, Loader2, RefreshCw, Search, Undo2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  countPendingPayments, loadJournal, undoAllocation,
} from '../../../lib/payments/actions.supabase';
import { defairePlacement } from '../../../lib/payments/refundActions.supabase';
import {
  LIBELLES_DE_TRI_JOURNAL, TAILLE_DE_PAGE, criteresParDefaut, libelleSens, lireRecherche,
  phraseDuJournal, type LigneDuJournal, type Sens, type TriDuJournal,
} from '../../../lib/payments/journal';
import { messageErreur } from '../../../lib/payments/erreur';
import CommunicationComplete from './CommunicationComplete';
import LienFacture from './LienFacture';
import { dateCourte, euros } from './money';

/** Comment le mouvement a été décidé, en français. */
const METHODES: Record<string, { label: string; classe: string }> = {
  structured: { label: 'communication structurée', classe: 'bg-green-100 text-green-800' },
  legacy_reference: { label: 'référence CDV lue', classe: 'bg-green-100 text-green-800' },
  manual: { label: 'décidé à la main', classe: 'bg-gray-100 text-gray-700' },
  /* Côté dépense, la « méthode » est la source du mouvement. */
  bank: { label: 'virement sortant', classe: 'bg-orange-100 text-orange-800' },
  stripe: { label: 'remboursement Stripe', classe: 'bg-orange-100 text-orange-800' },
};

const CIBLES: Record<string, string> = {
  invoice: 'facture',
  provision: 'provision',
  refund: 'remboursement',
  out_of_scope: 'hors périmètre',
};

export default function AllocationJournal({
  rafraichir, onVoirDansLaFile,
}: {
  rafraichir: number;
  /**
   * Emmène la même recherche dans la file de rapprochement.
   *
   * Demandé par le défaut du 19 septembre 2026 : une recherche ici ne trouvait
   * rien, et on en concluait que le versement manquait. Il attendait dans la
   * file — cet onglet ne montre que ce qui a été DÉCIDÉ. Dire combien de
   * mouvements attendent ne suffit pas : il faut pouvoir y aller, avec la
   * recherche déjà faite.
   */
  onVoirDansLaFile?: (recherche: string) => void;
}) {
  const [lignes, setLignes] = useState<LigneDuJournal[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [centimes, setCentimes] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [deplie, setDeplie] = useState<string | null>(null);

  const [sens, setSens] = useState<Sens | null>(null);
  const [tri, setTri] = useState<TriDuJournal>(criteresParDefaut().tri);
  /*
    Deux états pour la recherche : ce qui est TAPÉ, et ce qui est CHERCHÉ. Sans
    cette séparation, chaque frappe partait en requête sur 4 000 lignes.
    L'envoi se fait au `submit` — une recherche est un geste, pas un effet de
    bord de la frappe.
  */
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(0);
  /*
    Combien de mouvements NON ENCORE DÉCIDÉS répondent à la même recherche.

    Ce chiffre est la réponse au défaut du 19 septembre 2026 : « je cherche un
    versement de 110 euros pour ce parent mais il n'apparaît pas ». Il
    n'apparaissait pas parce qu'il attendait dans la file, et que ce journal ne
    porte que ce qui a été décidé. Un écran qui ne montre qu'une moitié de
    l'argent sans le dire fait conclure à un trou.
  */
  const [enFile, setEnFile] = useState<number | null>(null);

  /* Ce que la règle a compris de la saisie, pour le dire à l'écran. */
  const comprise = useMemo(() => lireRecherche(saisie), [saisie]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await loadJournal({
        sens, tri, recherche,
        taille: TAILLE_DE_PAGE,
        depuis: page * TAILLE_DE_PAGE,
      });
      setLignes(r.lignes);
      setTotal(r.total);
      setCentimes(r.centimesAffiches);
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture du journal impossible'));
    } finally {
      setChargement(false);
    }
  }, [sens, tri, recherche, page]);

  useEffect(() => { void charger(); }, [charger, rafraichir]);

  /*
    Lecture séparée, et volontairement : elle regarde une AUTRE population —
    les mouvements que personne n'a encore décidés. Un échec ici ne doit pas
    priver l'écran de son journal, d'où le `catch` qui repasse à `null` plutôt
    que d'afficher zéro. Zéro se lirait « il n'y en a pas », ce qu'on ne sait
    pas.
  */
  useEffect(() => {
    let annule = false;
    if (!recherche) { setEnFile(null); return; }
    (async () => {
      try {
        const { nombre } = await countPendingPayments(recherche);
        if (!annule) setEnFile(nombre);
      } catch {
        if (!annule) setEnFile(null);
      }
    })();
    return () => { annule = true; };
  }, [recherche, rafraichir]);

  /* Changer un critère ramène à la première page : rester en page 4 d'un
     résultat qui n'en a qu'une donne un écran vide sans explication. */
  const changerSens = (s: Sens | null) => { setSens(s); setPage(0); };
  const changerTri = (t: TriDuJournal) => { setTri(t); setPage(0); };
  const lancerRecherche = (texte: string) => { setRecherche(texte); setPage(0); };

  /**
   * Défaire une imputation, des deux côtés.
   *
   * ## Ce que « défaire » ne fait pas, et que le message doit dire
   *
   * Ni d'un côté ni de l'autre cela ne touche à la banque. L'argent est entré,
   * ou il est sorti ; aucun clic ne le rappelle. Ce qui se défait, c'est la
   * **pièce sur laquelle on l'a imputé** — et le montant retourne dans la file
   * correspondante, à replacer.
   *
   * ## Pourquoi les dépenses en étaient exclues
   *
   * Demandé le 21 septembre 2026 : « je peux défaire les autres mais pas les
   * dépenses visiblement alors que ça serait utile ». Le refus s'appuyait sur
   * « une dépense est un mouvement bancaire déjà parti » — vrai du virement,
   * faux de son imputation, et tout aussi vrai d'une entrée, qu'on défaisait
   * pourtant sans difficulté.
   *
   * La vraie cause était dans la vue : sa moitié « dépense » montrait un
   * virement, pas une décision, et `ligne_id` désignait le mouvement. Il n'y
   * avait littéralement rien à supprimer. Depuis
   * `20260921075716_defaire_une_depense_aussi`, elle montre le placement, donc
   * `defairePlacement` s'applique.
   */
  const defaire = async (l: LigneDuJournal) => {
    setEnCours(l.ligneId);
    try {
      if (l.sens === 'entree') {
        await undoAllocation(l.ligneId);
        toast.success('Imputation défaite : le montant retourne dans la file de rapprochement');
      } else {
        await defairePlacement(l.ligneId);
        toast.success('Imputation défaite : la dépense retourne dans « Dépenses à placer »');
      }
      await charger();
    } catch (e) {
      toast.error(messageErreur(e, 'Annulation refusée'));
    } finally {
      setEnCours(null);
    }
  };

  const pages = total !== null ? Math.ceil(total / TAILLE_DE_PAGE) : null;

  return (
    <div className="space-y-4">
      {/* --- Les filtres et la recherche --- */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            <Filtrer actif={sens === null} onClick={() => changerSens(null)}>
              Tout
            </Filtrer>
            <Filtrer actif={sens === 'entree'} onClick={() => changerSens('entree')}>
              <ArrowDownLeft className="h-3.5 w-3.5" />
              {libelleSens('entree')}
            </Filtrer>
            <Filtrer actif={sens === 'depense'} onClick={() => changerSens('depense')}>
              <ArrowUpRight className="h-3.5 w-3.5" />
              {libelleSens('depense')}
            </Filtrer>
          </div>

          <label className="ml-auto flex items-center gap-1.5 text-sm text-gray-600">
            <ArrowDownUp className="h-4 w-4 text-gray-400" />
            <select
              value={tri}
              onChange={(e) => changerTri(e.target.value as TriDuJournal)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
            >
              {(Object.keys(LIBELLES_DE_TRI_JOURNAL) as TriDuJournal[]).map((t) => (
                <option key={t} value={t}>{LIBELLES_DE_TRI_JOURNAL[t]}</option>
              ))}
            </select>
          </label>

          <button
            onClick={() => void charger()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Rafraîchir
          </button>
        </div>

        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => { e.preventDefault(); lancerRecherche(saisie); }}
        >
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              placeholder="Montant, compte, date, communication, n° de facture ou de note de crédit"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-8 text-sm"
            />
            {saisie && (
              <button
                type="button"
                onClick={() => { setSaisie(''); lancerRecherche(''); }}
                aria-label="Effacer la recherche"
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Search className="h-4 w-4" /> Chercher
          </button>
        </form>

        {/*
          Ce que la règle a compris. Une recherche qui interprète en silence est
          une recherche qu'on ne peut pas corriger : « 12/03/2026 » cherche une
          date, « BE64… » un compte, le reste du texte.
        */}
        {comprise.explication && (
          <p className="text-xs text-gray-500">{comprise.explication}</p>
        )}

        {/*
          L'autre moitié de l'argent. Ce journal ne porte que ce qui a été
          DÉCIDÉ ; un versement qui attend encore n'y est pas, par construction.
          Sans cette phrase, chercher un nom ici et n'en rien tirer se lit
          « le versement manque » — c'est exactement la conclusion tirée le
          19 septembre 2026, sur un virement qui était bien en base.
        */}
        {enFile !== null && enFile > 0 && (
          <p className="flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <Inbox className="h-4 w-4 shrink-0" />
            <span>
              <strong>{enFile} mouvement(s)</strong> répondant à cette recherche attendent encore
              dans la file : ils n'ont pas été rapprochés, donc ils ne peuvent pas figurer ici.
            </span>
            {onVoirDansLaFile && (
              <button
                onClick={() => onVoirDansLaFile(recherche)}
                className="ml-auto shrink-0 rounded-md border border-blue-300 bg-white px-3 py-1 text-xs font-medium text-blue-900 hover:bg-blue-100"
              >
                Voir dans la file
              </button>
            )}
          </p>
        )}
      </div>

      {erreur && (
        <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{erreur}</span>
        </p>
      )}

      {chargement ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture du journal…
        </div>
      ) : lignes.length === 0 && !erreur ? (
        <p className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
          {recherche
            ? `Aucun mouvement ne correspond à « ${recherche} ».`
            : 'Aucun mouvement dans cette catégorie.'}
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-700">
            {phraseDuJournal(lignes.length, total, centimes, euros)}
            {total !== null && total > lignes.length && (
              <span className="text-gray-500"> Le montant porte sur les lignes affichées.</span>
            )}
          </p>

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Sens</th>
                  <th className="px-4 py-2 text-left font-medium">Mouvement</th>
                  <th className="px-4 py-2 text-left font-medium">Communication</th>
                  <th className="px-4 py-2 text-left font-medium">Rattaché à</th>
                  <th className="px-4 py-2 text-right font-medium">Montant</th>
                  <th className="px-4 py-2 text-left font-medium">Décidé par</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.flatMap((l) => {
                  const m = METHODES[l.methode]
                    ?? { label: l.methode, classe: 'bg-gray-100 text-gray-700' };
                  const depense = l.sens === 'depense';

                  const ligne = (
                    <tr
                      key={l.ligneId}
                      className={clsx(
                        'align-top',
                        depense ? 'bg-orange-50/40' : l.cible === 'invoice' && 'bg-green-50/40',
                      )}
                    >
                      <td className="px-3 py-2">
                        <span
                          title={depense ? 'Argent sorti du compte' : 'Argent reçu'}
                          className={clsx(
                            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
                            depense ? 'bg-orange-100 text-orange-900' : 'bg-blue-100 text-blue-900',
                          )}
                        >
                          {depense
                            ? <ArrowUpRight className="h-3 w-3" />
                            : <ArrowDownLeft className="h-3 w-3" />}
                          {depense ? 'Dépense' : 'Entrée'}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                        {dateCourte(l.dateValeur)}
                        {l.mouvementCents !== l.montantCents && (
                          <span className="ml-1 text-xs text-gray-400">
                            (sur {euros(l.mouvementCents)})
                          </span>
                        )}
                        <span className="mt-0.5 block max-w-[14rem] truncate text-xs text-gray-500">
                          {l.nomContrepartie ?? (depense ? 'bénéficiaire inconnu' : 'émetteur inconnu')}
                        </span>
                        {l.compteContrepartie && (
                          <span className="mt-0.5 block font-mono text-xs text-gray-400">
                            {l.compteContrepartie}
                          </span>
                        )}
                      </td>

                      <td className="max-w-[16rem] px-4 py-2">
                        {/*
                          Le dépliage ne vaut que pour une entrée : la
                          communication complète se relit sur le PAIEMENT, et une
                          dépense n'en a pas.
                        */}
                        {l.paymentId ? (
                          <button
                            onClick={() => setDeplie(deplie === l.ligneId ? null : l.ligneId)}
                            title="Voir la communication en entier"
                            className="flex max-w-full items-start gap-1 text-left"
                          >
                            {deplie === l.ligneId
                              ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                              : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />}
                            <span className="truncate font-mono text-xs text-gray-700 hover:text-indigo-700">
                              {l.communication || '—'}
                            </span>
                          </button>
                        ) : (
                          <span className="block truncate font-mono text-xs text-gray-700">
                            {l.communication || '—'}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-2">
                        {l.pieceNumero && l.pieceType === 'facture' ? (
                          <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 font-medium">
                            <FileCheck2 className="h-3 w-3 text-green-900" />
                            <LienFacture
                              invoiceNumber={l.pieceNumero}
                              pdfUrl={l.piecePdf}
                              ton="succes"
                            />
                          </span>
                        ) : l.pieceNumero ? (
                          /* Une note de crédit : le numéro, sans lien — le PDF
                             d'une note ne vit pas au même endroit. */
                          <span className="inline-block rounded bg-orange-100 px-1.5 py-0.5 font-mono text-xs font-medium text-orange-900">
                            {l.pieceNumero}
                          </span>
                        ) : (
                          <span className="text-gray-700">{CIBLES[l.cible] ?? l.cible}</span>
                        )}

                        {l.famille ? (
                          <span className="mt-0.5 block text-xs text-gray-600">{l.famille}</span>
                        ) : depense ? (
                          /*
                            Aucune des 45 dépenses n'est rattachée : `refunds.user_id`
                            n'est jamais écrit, donc la provision de la famille
                            n'est pas réduite. On le DIT plutôt que de laisser
                            une case vide.
                          */
                          <span
                            title="Ce remboursement n'est rattaché à aucune famille : la provision n'en tient pas compte."
                            className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900"
                          >
                            non rattaché
                          </span>
                        ) : null}

                        {l.notes && (
                          <span className="mt-0.5 block text-xs text-gray-500">{l.notes}</span>
                        )}
                      </td>

                      <td className={clsx(
                        'whitespace-nowrap px-4 py-2 text-right font-medium tabular-nums',
                        depense ? 'text-orange-900' : 'text-gray-900',
                      )}>
                        {depense ? '−' : ''}{euros(l.montantCents)}
                      </td>

                      <td className="px-4 py-2">
                        <span className={clsx('inline-block rounded px-1.5 py-0.5 text-xs font-medium', m.classe)}>
                          {m.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-400">
                          {dateCourte(l.decideLe)}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <button
                          disabled={enCours === l.ligneId}
                          onClick={() => void defaire(l)}
                          title={depense
                            ? "Défaire cette imputation : le virement reste parti, il retourne dans « Dépenses à placer »"
                            : 'Défaire cette imputation : le montant retourne dans la file'}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {enCours === l.ligneId
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Undo2 className="h-3 w-3" />}
                          Défaire
                        </button>
                      </td>
                    </tr>
                  );

                  if (deplie !== l.ligneId || !l.paymentId) return [ligne];
                  return [
                    ligne,
                    <tr key={`${l.ligneId}-detail`}>
                      <td colSpan={7} className="bg-gray-50 px-4 py-3">
                        <CommunicationComplete
                          paymentId={l.paymentId}
                          communicationConnue={l.communication}
                        />
                      </td>
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* --- La pagination, parce que la liste est plus longue que l'écran --- */}
          {pages !== null && pages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <button
                disabled={page === 0}
                onClick={() => setPage((n) => Math.max(0, n - 1))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Précédentes
              </button>
              <span className="text-sm text-gray-600 tabular-nums">
                Page {page + 1} sur {pages}
              </span>
              <button
                disabled={page + 1 >= pages}
                onClick={() => setPage((n) => n + 1)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Suivantes
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Filtrer({
  actif, onClick, children,
}: { actif: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
        actif ? 'bg-indigo-600 text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
      )}
    >
      {children}
    </button>
  );
}
